import OnboardingApi from "./OnboardingApi";
import OnboardingForm from "./OnboardingForm";
import OnboardingProgress from "./OnboardingProgress";
import {
  buildAccountPayload,
  buildPayload,
  emptyAnswers,
  failureMessage,
  OnboardingMode,
  OnboardingWho,
  pinDigits,
  progressOf,
  refusedField,
  stepsFor,
  TOnboardingAnswers,
  TOnboardingLimits,
  TServerProgress,
  validateStep,
} from "./OnboardingRules";

/** The screens whose button stays grey until the answer is complete. */
const GATED_STEPS = ["terms", "parentName", "kidNames", "avatar", "childName"];

/** The account's consent and password, once given, are not revisited: no way back from there. */
const NO_BACK_STEPS = ["password", "who"];

/** The last screen of each path: its button sends the family, and so does Enter on the child's. */
const SUBMIT_STEPS = ["pin", "childName"];

/**
 * The class the header reads to drop the school and packages links while a child goes through
 * the signup. The body is outside the page's root, so this is the one place that touches it.
 */
const CHILD_PATH_CLASS = "family-child-path";

/** The class the header reads to trade the page's login for a logout once the account exists. */
const ACCOUNT_CLASS = "family-signup-account";

/**
 * Where a refused field sends the visitor back to. A field whose screen is not on this path (the
 * children's names on the child path, sent from the name screen) stays on the screen in front.
 */
const STEP_OF_FIELD: Record<string, string> = {
  email: "email",
  birthDate: "terms",
  terms: "terms",
  password: "password",
  parentName: "parentName",
  children: "kidNames",
  avatar: "avatar",
  pin: "pin",
};

/**
 * Walks the family onboarding: one screen at a time, each checked before the next. The account
 * is created as the password screen is left, so its confirmation email is on its way while the
 * family is named; the family is sent once at the end, and the server's answer says where the
 * page goes. Once the account exists, where the page is goes to the server at every screen:
 * the email's link, in any tab or on any device, lands the parent back on it.
 */
export default class OnboardingFlow {
  private answers: TOnboardingAnswers;
  /** The page came with the email: the email screen is not in the flow, until that email is refused. */
  private emailKnown: boolean;
  /** Where the account's page was, when the server kept it. */
  private readonly serverProgress: TServerProgress | null;
  /** « famille » when a school account chose this side on the login popup; the family's record carries it. */
  private history: string[] = [];
  private current: string;
  private busy = false;
  /** The account's record of where the page is, one write after the other, flushed before the family is sent. */
  private saving: Promise<void> = Promise.resolve();
  /** The PIN typed the first time, waiting for its confirmation. */
  private pinFirst: string | null = null;

  constructor(
    private readonly root: JQuery<HTMLElement>,
    private readonly form: OnboardingForm,
    private readonly api: OnboardingApi,
    /** "new" becomes "existing" the moment the account is created. */
    private mode: OnboardingMode,
    private readonly limits: TOnboardingLimits,
    private readonly locale: string,
    /** The reCAPTCHA token for the account call; resolves empty when the check is off. */
    private readonly captchaToken: () => Promise<string>,
    private readonly navigate: (url: string) => void,
    private readonly progress: OnboardingProgress = new OnboardingProgress(),
    /** Marks the body while the child path is on: the header hides what is the parent's. */
    private readonly markChildPath: (on: boolean) => void = (on) =>
      $(document.body).toggleClass(CHILD_PATH_CLASS, on),
    /** Marks the body once the account exists: the header offers the logout instead of the login. */
    private readonly markAccount: () => void = () =>
      $(document.body).addClass(ACCOUNT_CLASS),
  ) {
    const email = root.attr("data-email") ?? "";
    this.answers = emptyAnswers(email);
    this.emailKnown = email !== "";
    this.serverProgress = parseProgress(root.attr("data-progress") ?? "");
    this.current = this.steps[0];
    this.bind();
    this.restore(email);
    this.markChildPath(this.answers.who === "child");
    this.form.Show(this.current, this.canGoBack());
    this.refreshNext();
  }

  /**
   * A reload lands where the visitor was, with what they had answered: the tab's own record
   * first, the account's otherwise (a new tab, another device).
   */
  private restore(email: string): void {
    const saved = this.progress.Load(this.mode, email);
    if (saved !== null) {
      this.resume(saved.current, saved.history, {
        ...emptyAnswers(email),
        ...saved.answers,
        pin: "",
      });
      return;
    }
    if (this.serverProgress !== null) {
      this.resume(this.serverProgress.current, this.serverProgress.history, {
        ...this.answers,
        ...this.serverProgress.answers,
      });
    }
  }

  private resume(
    current: string,
    history: string[],
    answers: TOnboardingAnswers,
  ): void {
    this.answers = answers;
    if (!this.steps.includes(current)) {
      this.answers = emptyAnswers(this.answers.email);
      return;
    }
    this.history = history.filter((step) => this.steps.includes(step));
    this.current = current;
    // An avatar the tiles no longer offer is no answer: the screen asks again rather than
    // letting the server refuse it at the end
    if (
      this.answers.avatar !== null &&
      !this.form.HasAvatar(this.answers.avatar)
    ) {
      this.answers.avatar = null;
    }
    this.form.Fill(this.answers);
  }

  /** What is typed on a screen is the tab's business until the screen is left. */
  private rememberInTab(): void {
    this.progress.Save({
      mode: this.mode,
      email: this.answers.email,
      current: this.current,
      history: this.history,
      answers: this.answers,
    });
  }

  private remember(): void {
    this.progress.Save({
      mode: this.mode,
      email: this.answers.email,
      current: this.current,
      history: this.history,
      answers: this.answers,
    });
    // Nothing to keep it on before the account exists
    if (this.mode !== "new") {
      const progress = progressOf(this.current, this.history, this.answers);
      this.saving = this.saving.then(async () => {
        try {
          await this.api.SaveProgress(progress);
        } catch {
          // A screen the server did not hear of costs the parent that screen when the
          // email's link is clicked, nothing more: the page itself is not in the way
        }
      });
    }
  }

  private get steps(): string[] {
    return stepsFor(this.mode, this.emailKnown, this.answers.who);
  }

  private bind(): void {
    this.root.on("click", ".js-ob-next", () => this.next());
    this.root.on("click", ".js-ob-back", () => this.back());
    this.root.on("click", ".js-ob-who", (event) => {
      const who = $(event.currentTarget).attr("data-who");
      if (who === "parent" || who === "child") {
        this.choose(who);
      }
    });
    this.root.on("click", ".js-ob-avatar", (event) => {
      this.answers.avatar = $(event.currentTarget).attr("data-avatar") ?? null;
      this.form.SelectAvatar(this.answers.avatar);
      this.refreshNext();
      this.rememberInTab();
    });
    this.root.on("click", ".js-ob-kid-count", (event) => {
      this.answers.kidCount = Number($(event.currentTarget).attr("data-count"));
      this.form.SelectKidCount(this.answers.kidCount);
    });
    this.root.on("click", ".js-ob-pwd-toggle", () =>
      this.form.TogglePasswordVisibility(),
    );
    this.root.on("click", ".js-ob-submit", () => {
      void this.submit();
    });
    this.root.on("keydown", "input", (event) => {
      if (event.key !== "Enter") {
        return;
      }
      event.preventDefault();
      if (this.current === "childName") {
        void this.submit();
      } else if (!SUBMIT_STEPS.includes(this.current)) {
        this.next();
      }
    });
    this.root.on("input change", "input", () => {
      this.form.ReadInto(this.answers, !this.emailKnown);
      this.refreshNext();
      this.rememberInTab();
    });
    this.bindDateBoxes();
    this.bindPin();
  }

  /** A full box hands the caret to the next one; backspace on an empty one goes back. */
  private bindDateBoxes(): void {
    const boxes = [".js-ob-dob-day", ".js-ob-dob-month", ".js-ob-dob-year"];
    boxes.forEach((selector, i) => {
      this.root.on("input", selector, (event) => {
        const input = $(event.currentTarget);
        const digits = String(input.val() ?? "").replace(/\D/g, "");
        input.val(digits);
        const max = Number(input.attr("maxlength"));
        if (digits.length >= max && i < boxes.length - 1) {
          this.root
            .find(boxes[i + 1])
            .trigger("focus")
            .trigger("select");
        }
      });
      this.root.on("keydown", selector, (event) => {
        const empty = String($(event.currentTarget).val() ?? "") === "";
        if (event.key === "Backspace" && empty && i > 0) {
          event.preventDefault();
          this.root.find(boxes[i - 1]).trigger("focus");
        }
      });
    });
  }

  /**
   * The PIN is typed twice. The first four digits are held aside and the boxes empty for the
   * confirmation; the answer is kept only once both match, and the button follows.
   */
  private bindPin(): void {
    const input = this.form.pinInput;
    this.root.on("click", ".js-ob-pin-boxes", () => input.trigger("focus"));
    input.on("input", () => {
      const digits = pinDigits(String(input.val() ?? ""));
      input.val(digits);
      this.answers.pin = "";
      if (digits.length < 4) {
        this.form.RenderPin(digits, true, false);
        return;
      }
      if (this.pinFirst === null) {
        this.pinFirst = digits;
        input.val("");
        this.form.HideError("pin");
        this.form.ShowPinPhase("confirm");
        this.form.RenderPin("", true, false);
        return;
      }
      if (digits === this.pinFirst) {
        this.answers.pin = digits;
        this.form.RenderPin(digits, true, true);
        return;
      }
      this.resetPin();
      this.form.ShowError("pin", "pin-mismatch");
    });
    input.on("focus blur", (event) => {
      this.form.RenderPin(
        String(input.val() ?? ""),
        event.type === "focus",
        this.answers.pin !== "",
      );
    });
  }

  private resetPin(): void {
    this.pinFirst = null;
    this.answers.pin = "";
    this.form.pinInput.val("");
    this.form.ShowPinPhase("create");
    this.form.RenderPin("", true, false);
  }

  private next(): void {
    if (this.busy) {
      return;
    }
    this.form.ReadInto(this.answers, !this.emailKnown);
    const error = validateStep(this.current, this.answers, this.limits);
    if (error !== null) {
      this.form.ShowError(this.current, error);
      return;
    }
    if (this.current === "kidCount") {
      this.form.RenderKidNames(this.answers.kidCount, this.answers.kidNames);
      // One name per box, empty for now: what is kept has as many names as children
      this.form.ReadInto(this.answers, !this.emailKnown);
    }
    const index = this.steps.indexOf(this.current);
    const following = this.steps[index + 1];
    if (following === undefined) {
      return;
    }
    if (this.current === "email") {
      void this.leaveEmailStep(following);
      return;
    }
    if (this.current === "password") {
      void this.leavePasswordStep(following);
      return;
    }
    this.goTo(following);
  }

  /**
   * The account is created as the password screen is left: its confirmation email goes out now.
   * The page goes on as an account that exists, with the consent and the password behind it.
   */
  private async leavePasswordStep(following: string): Promise<void> {
    this.busy = true;
    this.form.SetBusy(true);
    try {
      const payload = buildAccountPayload(this.answers);
      const captcha = await this.captchaToken();
      if (captcha !== "") {
        payload.captcha = captcha;
      }
      await this.api.CreateAccount(payload);
      this.mode = "existing";
      this.markAccount();
      this.answers.password = "";
      this.answers.passwordConfirm = "";
      this.history = [];
      this.current = following;
      this.form.Show(following, false);
      this.refreshNext();
      this.remember();
    } catch (failure) {
      this.showFailure(failure);
    } finally {
      this.busy = false;
      this.form.SetBusy(false);
    }
  }

  /** An email that has an account is sent to the login, not through a signup it cannot finish. */
  private async leaveEmailStep(following: string): Promise<void> {
    this.busy = true;
    this.form.SetBusy(true);
    try {
      const answer = await this.api.CheckEmail(this.answers.email.trim());
      if (answer.status === "exists") {
        this.form.OfferLogin(this.answers.email.trim());
        return;
      }
      this.goTo(following);
    } catch (failure) {
      this.showFailure(failure);
    } finally {
      this.busy = false;
      this.form.SetBusy(false);
      this.refreshNext();
    }
  }

  private choose(who: OnboardingWho): void {
    this.answers.who = who;
    this.markChildPath(who === "child");
    this.goTo(this.steps[this.steps.indexOf("who") + 1]);
  }

  private goTo(step: string): void {
    this.history.push(this.current);
    this.current = step;
    if (step === "pin") {
      this.resetPin();
    }
    this.form.Show(step, this.canGoBack());
    this.refreshNext();
    this.remember();
  }

  private canGoBack(): boolean {
    return this.history.length > 0 && !NO_BACK_STEPS.includes(this.current);
  }

  /**
   * A gated screen's button follows the answer as it is typed. A name already in the family is
   * the one thing said right away: the other rules just keep the button grey.
   */
  private refreshNext(): void {
    if (!GATED_STEPS.includes(this.current)) {
      return;
    }
    this.form.ReadInto(this.answers, !this.emailKnown);
    const error = validateStep(this.current, this.answers, this.limits);
    this.form.EnableNext(this.current, error === null);
    if (error === "name-taken") {
      this.form.ShowError(this.current, error);
    } else {
      this.form.HideError(this.current);
    }
  }

  private back(): void {
    if (this.busy) {
      return;
    }
    const previous = this.history.pop();
    if (previous === undefined) {
      return;
    }
    // Back to « Qui veut continuer? » forgets the answer: the other card may be picked
    if (this.current === this.steps[this.steps.indexOf("who") + 1]) {
      this.answers.who = null;
      this.markChildPath(false);
    }
    this.current = previous;
    this.form.Show(previous, this.canGoBack());
    this.refreshNext();
    this.remember();
  }

  private async submit(): Promise<void> {
    if (this.busy) {
      return;
    }
    this.form.ReadInto(this.answers, !this.emailKnown);
    const error = validateStep(this.current, this.answers, this.limits);
    if (error !== null) {
      this.form.ShowError(this.current, error);
      return;
    }
    this.busy = true;
    this.form.SetBusy(true);
    try {
      await this.saving;
      const answer = await this.api.Complete(
        buildPayload(this.answers, this.mode, this.locale),
      );
      this.progress.Clear();
      this.navigate(answer.redirectUrl);
    } catch (failure) {
      this.busy = false;
      this.form.SetBusy(false);
      this.showFailure(failure);
      this.refreshNext();
    }
  }

  /**
   * A refused field sends the parent back to its screen with the server's words; the screens
   * before it become the way back. Anything else stays here with what the server said, or the
   * page's generic message.
   */
  private showFailure(failure: unknown): void {
    const refused = refusedField(failure);
    if (refused === null) {
      const message = failureMessage(failure);
      if (message === null) {
        this.form.ShowGenericError(this.current);
      } else {
        this.form.ShowError(this.current, message, true);
      }
      return;
    }
    // An email that came with the page and turns out taken: the page asks for it after all,
    // with the way to the login
    const askEmail = refused.field === "email" && !this.steps.includes("email");
    if (askEmail) {
      this.emailKnown = false;
      this.form.Fill(this.answers);
    }
    const target = STEP_OF_FIELD[refused.field];
    const step =
      target !== undefined && this.steps.includes(target)
        ? target
        : this.current;
    if (step !== this.current) {
      this.history = this.steps.slice(0, this.steps.indexOf(step));
      this.current = step;
      this.form.Show(step, this.canGoBack());
      this.refreshNext();
    }
    if (askEmail) {
      this.form.OfferLogin(this.answers.email.trim());
    }
    this.form.ShowError(step, refused.message, true);
  }
}

/** The account's record of where its page was, or null when there is none or it is unreadable. */
function parseProgress(raw: string): TServerProgress | null {
  if (raw === "") {
    return null;
  }
  try {
    const parsed = JSON.parse(raw) as Partial<TServerProgress>;
    if (typeof parsed.current !== "string" || parsed.answers === undefined) {
      return null;
    }
    return {
      current: parsed.current,
      history: Array.isArray(parsed.history) ? parsed.history : [],
      answers: {
        who: parsed.answers.who ?? null,
        parentName: parsed.answers.parentName ?? "",
        kidCount: parsed.answers.kidCount ?? 0,
        kidNames: parsed.answers.kidNames ?? [],
        avatar: parsed.answers.avatar ?? null,
        childName: parsed.answers.childName ?? "",
      },
    };
  } catch {
    return null;
  }
}
